import * as Three from 'three';
import store from '../../redux/store';
import { groupToGlbBlob } from '../../pages/Training/utils/gridpageUtils';
import {
  detachNestedShapeAssetsForExport,
  reattachDetachedShapeAssets,
} from './shapeAssetUtils';
import {
  addSceneMesh,
  add_new_assets,
  selected_object,
  setPushIntersectedObject,
  set_is_glb_image_editoropen,
} from '../../redux/Slice/gridSlice';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import { setLoadingMessage } from '../../redux/Slice/globalStates';
import { UpdateMaterial } from '../gridpagefunction/materialFunction';
import { exportTextMeshToGLB } from '../gridpagefunction/Text';
import { applyLightToNewObject } from '../../Hooks/useLight';
import { applyMeshShadowSettings } from '../../utils/ThreeJsUtils/lightShadowUtils';
import {
  isGridHistoryReady,
  suppressGridHistory,
} from '../../utils/gridHistorySuppress';
import {
  captureDiscreteHistoryBefore,
  recordDiscreteHistoryAfter,
} from '../../utils/gridHistoryDiscrete';
import { reconcileAssetsDataFromScene } from '../../utils/gridHistoryAssetsSync';

// Get next shape name that is not already taken in the scene or redux store
function getNextShapeName(action, scene = null) {
  const existingNames = new Set();

  // 1. Gather all existing names from the active 3D scene (if available)
  if (scene) {
    scene.traverse((obj) => {
      const name =
        obj.file_name ||
        obj.asset_file_name ||
        obj.userData?.file_name ||
        obj.name;
      if (name) {
        existingNames.add(name.trim().toLowerCase());
      }
    });
  }

  // 2. Gather all existing names from Redux meshes
  const state = store.getState();
  const sceneMeshes = state.data?.sceneMeshes || [];
  sceneMeshes.forEach((asset) => {
    const name = asset.file_name || asset.asset_file_name || asset.name;
    if (name) {
      existingNames.add(name.trim().toLowerCase());
    }
  });

  // 3. Find the first name in the series (e.g. "Cube", "Cube1", "Cube2", etc.) that is not used
  let candidate = action;
  if (!existingNames.has(candidate.toLowerCase())) {
    return candidate;
  }

  let counter = 1;
  while (true) {
    candidate = `${action}${counter}`;
    if (!existingNames.has(candidate.toLowerCase())) {
      return candidate;
    }
    counter++;
  }
}

// Keep legacy / unused functions as no-ops or simple wrappers to prevent export/import errors
function initializeCountersFromScene() {}
function initializeCountersFromRedux() {}
function resetShapeCounters() {}

/**
 * Export a shape to GLB without mutating the live scene.
 * Uses shared publish export path so MeshNormalMaterial and other types
 * are converted to GLTF-safe materials (avoids color.toArray errors).
 */
export const exportShapeAsGLB = async (mesh, shapeName, scene = null) => {
  if (!mesh) {
    throw new Error('exportShapeAsGLB: valid scene object is required');
  }

  const { detached, savedLocals } = detachNestedShapeAssetsForExport(mesh, scene);
  try {
    const blob = await groupToGlbBlob(mesh);
    const glbArrayBuffer = await blob.arrayBuffer();
    const file = new File([blob], `${shapeName}.glb`, {
      type: 'model/gltf-binary',
      lastModified: Date.now(),
    });

    return {
      file,
      arrayBuffer: glbArrayBuffer,
      blob,
    };
  } catch (error) {
    console.error('GLB export error:', error);
    throw error;
  } finally {
    reattachDetachedShapeAssets(mesh, detached, savedLocals);
  }
};

function serializeMaterial(material_settings) {
  if (!material_settings) return null;
  return {
    type: material_settings.type,
    albedo: material_settings.color,
    opacity: material_settings.opacity,
    transparent: material_settings.transparent,
    metallic: material_settings.metallic,
    roughness: material_settings.roughness,
    emission: material_settings.emission,
    map: material_settings.map ? true : false,
    normalMap: material_settings.normalMap ? true : false,
  };
}

export const HandleShapeClick = async ({
  action,
  scene,
  transformControls,
}) => {
  let geometry, mesh;
  const material = new Three.MeshStandardMaterial({ color: 0xffffff });

  switch (action) {
    case 'Plane':
      geometry = new Three.BoxGeometry(1, 1, 0.01);
      break;
    case 'Cube':
      geometry = new Three.BoxGeometry(1, 1, 1);
      break;
    case 'Sphere':
      geometry = new Three.SphereGeometry(0.5, 32, 32);
      break;
    case 'Cylinder':
      geometry = new Three.CylinderGeometry(0.5, 0.5, 1, 32);
      break;
    case 'Cone':
      geometry = new Three.ConeGeometry(0.5, 1, 32);
      break;
    case 'Pyramid': {
      let pyramidGeometry = new Three.ConeGeometry(0.5, 1, 4);
      pyramidGeometry.rotateY(Math.PI / 2);
      pyramidGeometry = pyramidGeometry.toNonIndexed();
      pyramidGeometry.computeVertexNormals();

      const pyramidMaterial = new Three.MeshStandardMaterial({
        color: 0xffffff,
        flatShading: true,
      });

      mesh = new Three.Mesh(pyramidGeometry, pyramidMaterial);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      break;
    }
    case 'Capsule': {
      const capsuleGroup = new Three.Group();
      const capsuleCylinder = new Three.Mesh(
        new Three.CylinderGeometry(0.5, 0.5, 0.5, 32),
        material
      );
      const capsuleTopSphere = new Three.Mesh(
        new Three.SphereGeometry(0.5, 32, 16, 0, Math.PI * 2, 0, Math.PI / 2),
        material
      );
      const capsuleBottomSphere = new Three.Mesh(
        new Three.SphereGeometry(
          0.5,
          32,
          16,
          0,
          Math.PI * 2,
          Math.PI / 2,
          Math.PI / 2
        ),
        material
      );
      capsuleTopSphere.position.y = 0.25;
      capsuleBottomSphere.position.y = -0.25;
      capsuleGroup.add(capsuleCylinder);
      capsuleGroup.add(capsuleTopSphere);
      capsuleGroup.add(capsuleBottomSphere);
      mesh = capsuleGroup;
      break;
    }
    case 'Torus':
      geometry = new Three.TorusGeometry(0.5, 0.2, 16, 100);
      break;
    case 'Rectangle':
      geometry = new Three.BoxGeometry(1.5, 1, 0.001);


       // Keep Z-axis world thickness fixed by placing mesh inside a Group
      const rectangleMesh = new Three.Mesh(geometry, material);
      rectangleMesh.castShadow = true;
      rectangleMesh.receiveShadow = true;
      rectangleMesh.onBeforeRender = function () {
        const parent = this.parent;
        const parentZ = parent ? parent.scale.z : 1;
        this.scale.z = parentZ === 0 ? 1 : 1 / parentZ;
      };

      mesh = new Three.Group();
      mesh.add(rectangleMesh);
      break;
    
    case 'Ellipse':
      geometry = new Three.SphereGeometry(0.5, 32, 32);
      geometry.scale(1, 0.7, 0.0001);
      // Keep Z-axis world thickness fixed by placing mesh inside a Group
      const ellipseMesh = new Three.Mesh(geometry, material);
      ellipseMesh.castShadow = true;
      ellipseMesh.receiveShadow = true;
      ellipseMesh.onBeforeRender = function () {
        const parent = this.parent;
        const parentZ = parent ? parent.scale.z : 1;
        this.scale.z = parentZ === 0 ? 1 : 1 / parentZ;
      };

      mesh = new Three.Group();
      mesh.add(ellipseMesh);
      break;
    case 'Triangle': {
      const shape = new Three.Shape();
      shape.moveTo(0, 1);
      shape.lineTo(-1, -1);
      shape.lineTo(1, -1);
      shape.lineTo(0, 1);
      geometry = new Three.ExtrudeGeometry(shape, {
        bevelEnabled: false,
      });

      geometry.scale(0.5, 0.5, 0.01); // ← make it smaller
      // Keep Z-axis world thickness fixed by placing mesh inside a Group
      const triangleMesh = new Three.Mesh(geometry, material);
      triangleMesh.castShadow = true;
      triangleMesh.receiveShadow = true;
      triangleMesh.onBeforeRender = function () {
        const parent = this.parent;
        const parentZ = parent ? parent.scale.z : 1;
        this.scale.z = parentZ === 0 ? 1 : 1 / parentZ;
      };

      mesh = new Three.Group();
      mesh.add(triangleMesh);
      break;
    }

    case 'Pentagon': {
      const shape = new Three.Shape();
      for (let i = 0; i < 5; i++) {
        const angle = (i / 5) * Math.PI * 2;
        const x = Math.cos(angle);
        const y = Math.sin(angle);
        i === 0 ? shape.moveTo(x, y) : shape.lineTo(x, y);
      }
      shape.closePath();
      geometry = new Three.ExtrudeGeometry(shape, {
        bevelEnabled: false,
      });
      geometry.scale(0.5, 0.5, 0.01);
      // Keep Z-axis world thickness fixed by placing mesh inside a Group
      const pentagonMesh = new Three.Mesh(geometry, material);
      pentagonMesh.castShadow = true;
      pentagonMesh.receiveShadow = true;
      pentagonMesh.onBeforeRender = function () {
        const parent = this.parent;
        const parentZ = parent ? parent.scale.z : 1;
        this.scale.z = parentZ === 0 ? 1 : 1 / parentZ;
      };

      mesh = new Three.Group();
      mesh.add(pentagonMesh);
      break;
    }
    case 'Star': {
      const shape = new Three.Shape();
      const spikes = 5,
        outerRadius = 0.5,
        innerRadius = 0.2;
      for (let i = 0; i < spikes * 2; i++) {
        const radius = i % 2 === 0 ? outerRadius : innerRadius;
        const angle = (i * Math.PI) / spikes;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        i === 0 ? shape.moveTo(x, y) : shape.lineTo(x, y);
      }
      shape.closePath();
      geometry = new Three.ExtrudeGeometry(shape, {
        depth: 0.01,
        bevelEnabled: false,
      });
      // Keep Z-axis world thickness fixed by placing mesh inside a Group
      const starMesh = new Three.Mesh(geometry, material);
      starMesh.castShadow = true;
      starMesh.receiveShadow = true;
      starMesh.onBeforeRender = function () {
        const parent = this.parent;
        const parentZ = parent ? parent.scale.z : 1;
        this.scale.z = parentZ === 0 ? 1 : 1 / parentZ;
      };

      mesh = new Three.Group();
      mesh.add(starMesh);
      break;
    }
    default:
      return;
  }

  if (!mesh) {
    mesh = new Three.Mesh(geometry, material);
  }

  // Get next available shape name
  const shapeName = getNextShapeName(action, scene);
  const tempId = `temp-${Date.now()}-${shapeName}`;

  store.dispatch(selected_object(null));
  store.dispatch(setLoadingMessage(true));

  mesh.castShadow = true;

  const historyBefore =
    isGridHistoryReady() && scene ? captureDiscreteHistoryBefore(scene) : null;
  const endHistorySuppress =
    historyBefore != null ? suppressGridHistory() : null;

  try {
    // Set basic mesh properties
    mesh.name = tempId;
    mesh.objType = '3d';
    mesh.url = tempId;
    mesh.deleteType = 'mesh';
    mesh._id = tempId;
    mesh.isShapes = true;

    // Set file names - these will be saved to backend
    mesh.file_name = shapeName;
    mesh.asset_file_name = shapeName;
    mesh.mesh_code = action;
    mesh.glb_type = 'shapes';
    mesh.createdAt = new Date().toISOString();

    mesh.traverse(function (child) {
      child.castShadow = true;
      child.receiveShadow = true;
    });
    applyMeshShadowSettings(mesh);

    mesh.position.set(0, 0, 0);
    mesh.temp_file_name = tempId;

    scene?.add(mesh);
    store.dispatch(setPushIntersectedObject(mesh));
    store.dispatch(set_is_glb_image_editoropen(true));

    // Store metadata in userData
    mesh.userData = {
      type: 'shape',
      glb_type: 'shapes',
      mesh_code: action,
      isShapes: true,
      file_name: shapeName,
      material_settings: {
        id: tempId,
        newId: tempId,
        shader: 'Standard',
        albedo: '#FFFFFF',
        side: 'Front',
        transparency: false,
        opacity: 1,
        metallic: { metalness: 0.5, roughness: 0.5, map: '' },
        normal_map: 'Unselected',
        textureImage: null,
        added: true,
      },
    };

    UpdateMaterial({ object: mesh, addData: mesh.userData.material_settings });

    store.dispatch(addSceneMesh(mesh));
    await store.dispatch(add_new_assets({ type: 'mesh', data: mesh })).unwrap();
    selectObjectInScene(tempId);
  } catch (error) {
    console.error('Failed to create shape:', error);

    // Fallback setup
    mesh.name = tempId;
    mesh.objType = '3d';
    mesh.url = tempId;
    mesh.deleteType = 'mesh';
    mesh._id = tempId;
    mesh.isShapes = true;

    mesh.file_name = shapeName;
    mesh.asset_file_name = shapeName;
    mesh.mesh_code = action;
    mesh.glb_type = 'shapes';
    mesh.createdAt = new Date().toISOString();
    mesh.export_error = error.message;

    mesh.traverse(function (child) {
      child.castShadow = true;
      child.receiveShadow = true;
    });
    applyMeshShadowSettings(mesh);

    mesh.position.set(0, 0, 0);
    scene?.add(mesh);

    store.dispatch(setPushIntersectedObject(mesh));
    store.dispatch(set_is_glb_image_editoropen(true));

    mesh.userData = {
      type: 'shape',
      glb_type: 'shapes',
      mesh_code: action,
      isShapes: true,
      file_name: shapeName,
      material_settings: {
        shader: 'Standard',
        albedo: '#FFFFFF',
        metallic: { metalness: 0.5, roughness: 0.5, map: '' },
        smoothness: 'Reflectivity',
        normal_map: 'Unselected',
        transparency: 'Alpha',
        textureImage: null,
      },
    };

    store.dispatch(addSceneMesh(mesh));
    await store.dispatch(add_new_assets({ type: 'mesh', data: mesh })).unwrap();
    selectObjectInScene(tempId);
  } finally {
    if (endHistorySuppress) {
      endHistorySuppress();
      if (historyBefore && scene) {
        reconcileAssetsDataFromScene(scene);
        recordDiscreteHistoryAfter(historyBefore, scene, 'add shape');
      }
    }
  }

  if (transformControls) {
    transformControls.detach();
    transformControls.attach(mesh);
    scene.add(transformControls);
  }

  store.dispatch(setLoadingMessage(false));

  return mesh;
};

export function resetShapeCountersAndScene(scene) {
  if (scene) {
    const toRemove = [];
    scene.traverse((obj) => {
      if (obj.userData && obj.userData.type === 'shape') {
        toRemove.push(obj);
      }
    });
    toRemove.forEach((obj) => scene.remove(obj));
  }
  resetShapeCounters();
}

export {
  resetShapeCounters,
  initializeCountersFromScene,
  initializeCountersFromRedux,
};
